import React, {useState, useEffect, useMemo, useCallback, memo} from 'react';
import {createPortal} from 'react-dom';
import {useQuery, useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {X, Loader, AlertCircle, Trophy, Store, Check, ChevronLeft} from 'lucide-react';
import {api} from '../../../config/api';
import secureStorage from '../../../utils/secureStorage';
import {toLocalDateStr} from '../../../utils/dateHelpers';
import {SummaryStrip, thClass} from '../../../stock/StockUI';
import DateRangePicker from './DateRangePicker';

const MIN_ACCOUNTS = 2;
const MAX_ACCOUNTS = 10;

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + " DH";

// Small uppercase caption, same as the SummaryStrip labels
const labelClass = 'text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider';
const sectionClass = 'px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700';

// Label / value line inside a hairline list
const DetailRow = ({label, value, valueClass = 'text-gray-900 dark:text-gray-100'}) => (
    <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
        <span className={`text-sm font-medium tabular-nums ${valueClass}`}>{value}</span>
    </div>
);

const AccountCompareCard = memo(({account, t}) => {
    const marge = account.marge || {};
    const lowCoverage = (marge.couverture_cout_pct ?? 0) < 50;

    return (
        <div>
            {/* KPIs : bande à filets comme le Stock */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-gray-200 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                {[
                    {key: 'caTtc', value: formatCurrency(account.ca_ttc), color: 'text-primary-dark dark:text-primary'},
                    {key: 'caHt', value: formatCurrency(account.ca_ht)},
                    {key: 'nbVentes', value: account.nb_ventes ?? 0},
                    {key: 'panierMoyen', value: formatCurrency(account.panier_moyen_ttc)},
                    {key: 'nbClients', value: account.nb_clients_uniques ?? 0},
                    {key: 'totalRemises', value: formatCurrency(account.total_remises)},
                ].map(kpi => (
                    <div key={kpi.key} className="bg-white dark:bg-gray-800 px-4 sm:px-6 py-3">
                        <span className={`block ${labelClass}`}>{t(`compareAccounts.${kpi.key}`)}</span>
                        <span className={`block text-lg font-bold tabular-nums truncate ${kpi.color || 'text-gray-900 dark:text-gray-100'}`}>{kpi.value}</span>
                    </div>
                ))}
            </div>

            <div className="divide-y divide-gray-200 dark:divide-gray-700 border-b border-gray-200 dark:border-gray-700">
                {account.annulations && account.annulations.nombre > 0 && (
                    <DetailRow
                        label={t("compareAccounts.cancellations")}
                        value={`${account.annulations.nombre} · ${formatCurrency(account.annulations.montant)}`}
                        valueClass="text-red-600 dark:text-red-400"
                    />
                )}
                <DetailRow label={t("compareAccounts.margin")} value={formatCurrency(marge.marge_estimee)}
                           valueClass="font-semibold text-gray-900 dark:text-gray-100"/>
                <DetailRow label={t("compareAccounts.marginPct")} value={`${(marge.marge_pct ?? 0).toFixed(1)}%`}/>
                <DetailRow label={t("compareAccounts.stockValue")} value={formatCurrency(account.valeur_stock_estimee)}/>
                <DetailRow
                    label={t("compareAccounts.coverage")}
                    value={
                        <span className="inline-flex items-center gap-1.5">
                            {lowCoverage && <AlertCircle className="w-3.5 h-3.5 flex-shrink-0"/>}
                            {(marge.couverture_cout_pct ?? 0).toFixed(0)}%
                            {lowCoverage && ` — ${t("compareAccounts.lowCoverageWarning")}`}
                        </span>
                    }
                    valueClass={lowCoverage ? 'text-orange-500' : 'text-gray-700 dark:text-gray-300'}
                />
            </div>

            {/* Top produits */}
            <div className="px-4 sm:px-6 pt-4 pb-2">
                <p className={labelClass}>{t("compareAccounts.topProducts")}</p>
            </div>
            {account.top_produits?.length > 0 ? (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {account.top_produits.map((p, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 text-sm">
                            <span className="truncate text-gray-900 dark:text-gray-100">{p.produit}</span>
                            <span className="flex-shrink-0 tabular-nums text-gray-500 dark:text-gray-400">
                                {p.quantite} · <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(p.ca)}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="px-4 sm:px-6 pb-4 text-sm text-gray-500 dark:text-gray-400">{t("compareAccounts.noTopProducts")}</p>
            )}
        </div>
    );
});

const num = (v) => Number(v) || 0;

// Higher is better for every highlighted metric; the rest are shown neutrally.
const COMPARE_ROWS = [
    {key: 'caTtc', get: a => num(a.ca_ttc), fmt: formatCurrency, best: true},
    {key: 'caHt', get: a => num(a.ca_ht), fmt: formatCurrency, best: true},
    {key: 'nbVentes', get: a => num(a.nb_ventes), fmt: v => v, best: true},
    {key: 'panierMoyen', get: a => num(a.panier_moyen_ttc), fmt: formatCurrency, best: true},
    {key: 'nbClients', get: a => num(a.nb_clients_uniques), fmt: v => v, best: true},
    {key: 'totalRemises', get: a => num(a.total_remises), fmt: formatCurrency},
    {key: 'margin', get: a => num(a.marge?.marge_estimee), fmt: formatCurrency, best: true},
    {key: 'marginPct', get: a => num(a.marge?.marge_pct), fmt: v => `${v.toFixed(1)}%`, best: true},
    {key: 'coverage', get: a => num(a.marge?.couverture_cout_pct), fmt: v => `${v.toFixed(0)}%`},
    {key: 'stockValue', get: a => num(a.valeur_stock_estimee), fmt: formatCurrency},
];

const CompareTable = memo(({accounts, t}) => {
    const rows = useMemo(() => COMPARE_ROWS.map(row => {
        const values = accounts.map(row.get);
        const max = Math.max(...values);
        // Highlight only when there is a real winner (positive and not everyone tied)
        const hasWinner = row.best && max > 0 && new Set(values).size > 1;
        return {...row, values, max, hasWinner};
    }), [accounts]);

    const stickyBg = 'bg-white dark:bg-gray-800';

    return (
        <div>
            <div className="px-4 sm:px-6 pt-4 pb-2">
                <p className={labelClass}>{t("compareAccounts.overview")}</p>
            </div>
            <div className="overflow-x-auto border-y border-gray-200 dark:border-gray-700">
                <table className="w-full border-collapse">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                            <th className={`${thClass} sticky left-0 z-[1] bg-gray-50 dark:bg-gray-700`}/>
                            {accounts.map((acc, i) => (
                                <th key={acc.account_id} scope="col" className={`${thClass} text-right whitespace-nowrap`}>
                                    <span className="inline-block max-w-[9rem] truncate align-bottom">{i + 1}. {acc.account_name}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {rows.map(row => (
                            <tr key={row.key}>
                                <th scope="row"
                                    className={`sticky left-0 z-[1] px-6 py-3 text-left text-sm font-medium whitespace-nowrap text-gray-500 dark:text-gray-400 ${stickyBg}`}>
                                    {t(`compareAccounts.${row.key}`)}
                                </th>
                                {row.values.map((v, i) => {
                                    const isBest = row.hasWinner && v === row.max;
                                    return (
                                        <td key={accounts[i].account_id}
                                            className={`px-6 py-3 text-right text-sm whitespace-nowrap tabular-nums ${
                                                isBest
                                                    ? 'text-primary-dark dark:text-primary font-semibold bg-primary/5'
                                                    : 'text-gray-700 dark:text-gray-300'
                                            }`}>
                                            {row.fmt(v)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
});

const CompareResults = ({result, orderedAccounts, t}) => {
    // One tab per compared account — defaults to the top-ranked one (index 0
    // of orderedAccounts, already sorted by the backend's classement.ordre).
    const [activeId, setActiveId] = useState(orderedAccounts[0]?.account_id ?? null);

    // The parent re-mounts this component (via `key`) on every new result,
    // so the active tab resets without an extra effect/render.
    if (!result) return null;
    const {classement} = result;
    const criterionLabel = classement?.critere === 'marge_estimee'
        ? t("compareAccounts.criterionMarge")
        : t("compareAccounts.criterionCa");

    const activeIndex = Math.max(0, orderedAccounts.findIndex(a => a.account_id === activeId));
    const activeAccount = orderedAccounts[activeIndex] || orderedAccounts[0];
    const best = orderedAccounts[0];

    return (
        <div>
            {/* Winner : même bande à filets que le Stock */}
            {classement && best && (
                <SummaryStrip items={[
                    {
                        key: 'best',
                        label: t("compareAccounts.best"),
                        value: <span className="inline-flex items-center gap-1.5 max-w-full"><Trophy className="w-4 h-4 flex-shrink-0"/><span className="truncate">{best.account_name || '—'}</span></span>,
                        hint: t("compareAccounts.rankedOn", {criterion: criterionLabel}),
                        color: 'text-primary-dark dark:text-primary',
                    },
                    {key: 'caTtc', label: t("compareAccounts.caTtc"), value: formatCurrency(best.ca_ttc)},
                    {key: 'margin', label: t("compareAccounts.margin"), value: formatCurrency(best.marge?.marge_estimee)},
                ]}/>
            )}

            <CompareTable accounts={orderedAccounts} t={t}/>

            {/* One tab per account */}
            <div className="flex items-center overflow-x-auto border-b border-gray-200 dark:border-gray-700 mt-4">
                {orderedAccounts.map((acc, index) => {
                    const isBest = classement?.meilleur_point_de_vente === acc.account_id;
                    const isActive = acc.account_id === activeAccount?.account_id;
                    return (
                        <button
                            key={acc.account_id}
                            type="button"
                            onClick={() => setActiveId(acc.account_id)}
                            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 sm:px-6 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                                isActive
                                    ? 'border-primary text-primary bg-primary/5'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                        >
                            <span className="tabular-nums">{index + 1}.</span>
                            <span className="max-w-[9rem] truncate">{acc.account_name}</span>
                            {isBest && <Trophy className="w-3 h-3 text-primary flex-shrink-0"/>}
                        </button>
                    );
                })}
            </div>

            {activeAccount && (
                <AccountCompareCard key={activeAccount.account_id} account={activeAccount} t={t}/>
            )}
        </div>
    );
};

const CompareAccountsModal = ({isOpen, onClose, isDarkMode, initialDateRange}) => {
    const {t} = useTranslation("activite");
    const [step, setStep] = useState('select');
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [dateRange, setDateRange] = useState(
        initialDateRange || [{startDate: new Date(), endDate: new Date(), key: 'selection'}]
    );
    const [result, setResult] = useState(null);
    const [resultVersion, setResultVersion] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');

    // Pre-select the currently active account so the user only has to add the others
    useEffect(() => {
        if (!isOpen) return;
        secureStorage.getItem("selectedAccount").then(acc => {
            if (acc?.id) {
                setSelectedIds(prev => (prev.size === 0 ? new Set([acc.id]) : prev));
            }
        });
    }, [isOpen]);

    const {data: accounts = [], isLoading: isLoadingAccounts, error: accountsError} = useQuery({
        queryKey: ['accounts-list-for-compare'],
        queryFn: async () => {
            const response = await api.get('/accounts', {
                headers: {'Accept': 'application/json'}
            });
            return response.data || [];
        },
        enabled: isOpen,
        staleTime: 60000,
    });

    const compareMutation = useMutation({
        mutationFn: async () => {
            const accountIds = Array.from(selectedIds).join(',');
            const startDate = toLocalDateStr(dateRange[0].startDate);
            const endDate = toLocalDateStr(dateRange[0].endDate);
            const response = await api.get('/accounts/compare', {
                params: {accountIds, startDate, endDate},
                headers: {'Accept': 'application/json'}
            });
            return response.data;
        },
        onSuccess: (data) => {
            setResult(data);
            setResultVersion(v => v + 1);
            setStep('results');
        },
        onError: (error) => {
            setErrorMsg(
                error.response?.data?.detail ||
                error.response?.data?.message ||
                t("compareAccounts.errorGeneric")
            );
        }
    });

    // Reset transient state whenever the modal is (re)opened. Resetting the
    // mutation also detaches it, so a response from a previous, closed session
    // can't push the reopened modal to the results step.
    useEffect(() => {
        if (isOpen) {
            setStep('select');
            setResult(null);
            setErrorMsg('');
            compareMutation.reset();
            // Follow the page's current period rather than the one at first mount
            if (initialDateRange) setDateRange(initialDateRange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Escape to close + lock background scroll while open
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen, onClose]);

    const toggleAccount = useCallback((id) => {
        setErrorMsg('');
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else if (next.size < MAX_ACCOUNTS) {
                next.add(id);
            }
            return next;
        });
    }, []);

    const canSubmit = selectedIds.size >= MIN_ACCOUNTS && selectedIds.size <= MAX_ACCOUNTS;

    const handleSubmit = () => {
        setErrorMsg('');
        if (!canSubmit) {
            setErrorMsg(
                selectedIds.size < MIN_ACCOUNTS
                    ? t("compareAccounts.errorMinAccounts")
                    : t("compareAccounts.errorMaxAccounts")
            );
            return;
        }
        compareMutation.mutate();
    };

    const orderedAccounts = useMemo(() => {
        if (!result?.accounts) return [];
        if (!result?.classement?.ordre?.length) return result.accounts;
        const byId = new Map(result.accounts.map(a => [a.account_id, a]));
        const ordered = result.classement.ordre.map(id => byId.get(id)).filter(Boolean);
        const seen = new Set(ordered);
        result.accounts.forEach(a => {
            if (!seen.has(a)) ordered.push(a);
        });
        return ordered;
    }, [result]);


    if (!isOpen) return null;

    const iconButtonClass = 'w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700';

    // Portaled: the page-transition transform would offset `fixed` otherwise
    return createPortal(
        <div
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black bg-opacity-50"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div role="dialog" aria-modal="true" aria-label={t("compareAccounts.title")}
                 className="w-full sm:max-w-3xl lg:max-w-5xl sm:mx-4 max-h-[92vh] sm:max-h-[85vh] rounded-t-2xl sm:rounded-lg shadow-lg overflow-hidden flex flex-col bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {/* Header — same as the shared Modal: centred title, back left, close right */}
                <div className="relative px-12 py-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 text-center">
                    {step === 'results' && (
                        <button
                            type="button"
                            onClick={() => setStep('select')}
                            aria-label={t("compareAccounts.back")}
                            className={`absolute left-4 top-1/2 -translate-y-1/2 ${iconButtonClass}`}
                        >
                            <ChevronLeft className="w-5 h-5"/>
                        </button>
                    )}
                    <h3 className="text-lg font-medium text-gray-800 dark:text-white truncate">{t("compareAccounts.title")}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t("compareAccounts.close")}
                        className={`absolute right-4 top-1/2 -translate-y-1/2 ${iconButtonClass}`}
                    >
                        <X className="w-4 h-4"/>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto">
                    {step === 'select' ? (
                        <div>
                            <div className={sectionClass}>
                                <p className={`${labelClass} mb-2`}>{t("compareAccounts.period")}</p>
                                <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} isDarkMode={isDarkMode}/>
                            </div>

                            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 pt-4 pb-2">
                                <p className={labelClass}>{t("compareAccounts.selectAccounts")}</p>
                                <span className={`text-xs font-semibold tabular-nums ${canSubmit ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`}>
                                    {t("compareAccounts.selectedCount", {count: selectedIds.size})}
                                </span>
                            </div>

                            {isLoadingAccounts ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader className="w-5 h-5 text-primary animate-spin"/>
                                </div>
                            ) : accountsError ? (
                                <p className="px-4 sm:px-6 pb-4 text-sm text-red-500">{t("compareAccounts.errorGeneric")}</p>
                            ) : accounts.length === 0 ? (
                                <p className="px-4 sm:px-6 pb-4 text-sm text-gray-500 dark:text-gray-400">
                                    {t("compareAccounts.noAccounts")}
                                </p>
                            ) : (
                                <div className="max-h-72 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                                    {accounts.map(acc => {
                                        const checked = selectedIds.has(acc.id);
                                        return (
                                            <label
                                                key={acc.id}
                                                className={`flex items-center gap-3 px-4 sm:px-6 py-3 cursor-pointer transition-colors ${
                                                    checked ? 'bg-primary/5' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="hidden"
                                                    checked={checked}
                                                    onChange={() => toggleAccount(acc.id)}
                                                />
                                                <span className={`w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${
                                                    checked ? 'bg-primary border-primary' : 'border-gray-300 dark:border-gray-600'
                                                }`}>
                                                    {checked && <Check className="w-3 h-3 text-white"/>}
                                                </span>
                                                <Store className="w-4 h-4 flex-shrink-0 text-gray-400"/>
                                                <span className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">
                                                    {acc.name}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}

                            {errorMsg && (
                                <div className="flex items-center gap-2 px-4 sm:px-6 py-3 text-sm text-red-500">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0"/>
                                    <span>{errorMsg}</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <CompareResults key={resultVersion} result={result} orderedAccounts={orderedAccounts} t={t}/>
                    )}
                </div>

                {/* Footer — "Annuler | Comparer", like the catalogue popups */}
                {step === 'select' && (
                    <div className="flex-shrink-0 grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="py-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            {t("compareAccounts.cancel")}
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={compareMutation.isLoading}
                            className="py-4 font-medium border-l border-gray-200 dark:border-gray-700 text-primary hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {compareMutation.isLoading && <Loader className="w-4 h-4 animate-spin"/>}
                            {compareMutation.isLoading ? t("compareAccounts.loading") : t("compareAccounts.compareButton")}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default CompareAccountsModal;
